/**
 * @file documents-query.ts
 * @description Состояние реестра документов в адресе страницы (phase19.31,
 * зона «Клиенты и сделки», часть 3): поиск, вид, статус, страница и её размер.
 *
 * Как у реестров сделок и клиентов (`lib/crm-list-query.ts`): F5 и «назад»
 * из карточки документа возвращают тот же отбор и ту же страницу; значения
 * по умолчанию в адрес не пишутся; неизвестный вид или статус из адреса —
 * «все». Поиск сервер ведёт по номеру документа и контрагенту (имя клиента,
 * название или ИНН юрлица — контракт §19).
 */

import { DOCUMENT_KINDS, DOCUMENT_STATUSES } from "./document-view";
import type { DocumentKind, DocumentStatus, listDocuments } from "../../../lib/api-crm";
import { CRM_DEFAULT_PAGE_SIZE, pageSizeFromParam, pageToOffset, positiveInt } from "../../../lib/crm-list-query";

export interface DocumentsRegistryQuery {
  /** Номер документа или контрагент (имя, название, ИНН). */
  search: string;
  /** Вид; пусто — все виды. */
  kind: "" | DocumentKind;
  /** Статус; пусто — все статусы. */
  status: "" | DocumentStatus;
  /** Номер страницы с единицы. */
  page: number;
  pageSize: number;
}

/** Имена параметров адреса — реестром, а не строками по коду. */
const KEY = { search: "q", kind: "kind", status: "status", page: "page", pageSize: "size" } as const;

/** Значение из адреса, только если оно из списка допустимых. */
function oneOf<T extends string>(raw: string | null, allowed: readonly T[]): "" | T {
  return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : "";
}

export function parseDocumentsQuery(params: URLSearchParams): DocumentsRegistryQuery {
  return {
    search: (params.get(KEY.search) ?? "").trim(),
    kind: oneOf(params.get(KEY.kind), DOCUMENT_KINDS),
    status: oneOf(params.get(KEY.status), DOCUMENT_STATUSES),
    page: positiveInt(params.get(KEY.page), 1),
    pageSize: pageSizeFromParam(params.get(KEY.pageSize)),
  };
}

export function writeDocumentsQuery(query: DocumentsRegistryQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.search) params.set(KEY.search, query.search);
  if (query.kind) params.set(KEY.kind, query.kind);
  if (query.status) params.set(KEY.status, query.status);
  if (query.page > 1) params.set(KEY.page, String(query.page));
  if (query.pageSize !== CRM_DEFAULT_PAGE_SIZE) params.set(KEY.pageSize, String(query.pageSize));
  return params;
}

/** Параметры запроса к серверу. */
export function toListDocumentsParams(query: DocumentsRegistryQuery): NonNullable<Parameters<typeof listDocuments>[0]> {
  return {
    search: query.search || undefined,
    kind: query.kind || undefined,
    status: query.status || undefined,
    ...pageToOffset(query.page, query.pageSize),
  };
}

/** Сколько фильтров спрятано в шторку на телефоне: вид и статус. */
export function documentsSheetFilterCount(query: DocumentsRegistryQuery): number {
  return [query.kind !== "", query.status !== ""].filter(Boolean).length;
}

/** Список сужен фильтром — от этого зависит текст пустого списка. */
export function documentsQueryFiltered(query: DocumentsRegistryQuery): boolean {
  return query.search !== "" || documentsSheetFilterCount(query) > 0;
}

/** «Сбросить фильтры»: все документы, размер страницы сохраняется. */
export function resetDocumentsQuery(query: DocumentsRegistryQuery): DocumentsRegistryQuery {
  return { search: "", kind: "", status: "", page: 1, pageSize: query.pageSize };
}
